import { useId } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Locale } from './types';
import { SUPPLY_PAGE_SIZES, type SupplyPaginationResult } from './useSupplyPagination';
import './supplyPagination.css';

export interface SupplyPaginationProps<T> {
  pagination: SupplyPaginationResult<T>;
  locale: Locale;
  onNavigate?: () => void;
}

export function SupplyPagination<T>({ pagination, locale, onNavigate }: SupplyPaginationProps<T>) {
  const selectId = useId();
  const es = locale === 'es';
  const { page, pageSize, total, totalPages, start, end, setPage, setPageSize } = pagination;
  if (!total) return null;

  const navigate = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    onNavigate?.();
  };
  const resize = (nextPageSize: number) => {
    if (nextPageSize === pageSize || !SUPPLY_PAGE_SIZES.some(size => size === nextPageSize)) return;
    setPageSize(nextPageSize);
    onNavigate?.();
  };

  return <footer className="supply-pagination">
    <p className="supply-pagination-range" role="status" aria-live="polite" aria-atomic="true">
      {es ? `Mostrando ${start}–${end} de ${total} ${total === 1 ? 'insumo' : 'insumos'}` : `Showing ${start}–${end} of ${total} ${total === 1 ? 'supply' : 'supplies'}`}
    </p>
    <div className="supply-pagination-controls">
      <div className="supply-pagination-size">
        <label htmlFor={selectId}>{es ? 'Por página' : 'Per page'}</label>
        <select id={selectId} value={pageSize} aria-label={es ? 'Insumos por página' : 'Supplies per page'} onChange={event => resize(Number(event.target.value))}>
          {SUPPLY_PAGE_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
        </select>
      </div>
      {totalPages > 1 && <nav className="supply-pagination-navigation" aria-label={es ? 'Paginación de insumos' : 'Supply pagination'}>
        <button type="button" disabled={page === 1} aria-label={es ? 'Página anterior' : 'Previous page'} onClick={() => navigate(page - 1)}><ChevronLeft size={18} strokeWidth={1.75} aria-hidden="true" /></button>
        <span className="supply-pagination-page">{es ? `Página ${page} de ${totalPages}` : `Page ${page} of ${totalPages}`}</span>
        <button type="button" disabled={page === totalPages} aria-label={es ? 'Página siguiente' : 'Next page'} onClick={() => navigate(page + 1)}><ChevronRight size={18} strokeWidth={1.75} aria-hidden="true" /></button>
      </nav>}
    </div>
  </footer>;
}
